<script lang="ts">
import Combobox from '@/components/v2/Combobox.vue';
import { X, Trash2 } from 'lucide-vue-next';
import AppButtonSelect from '@/components/v2/AppButtonSelect.vue';

const SECURITY_ROLES = ['admin', 'broker', 'client', 'market'];

export default {
  name: 'CreateUpdateUserModal',
  components: {
    Combobox,
    X,
    Trash2,
    AppButtonSelect,
  },
};
</script>

<script setup lang="ts">
import { ref, watch, computed } from 'vue';
import AppModal from '@/components/v2/AppModal.vue';
import { permissionService, carrierService, wholesalerGroupService } from '@/services/api';
import { UserPermissionDescriptions, UserPermission, AppRole } from '@/types/UserPermissions';
import { AccountInfo } from '@/types/AccountSummary';
import { onMounted, onUnmounted } from 'vue';
import { SelectOption } from '@/types/components/Select';
import z, { SafeParseError, ZodFormattedError } from 'zod';
import { AccountSelectOption, UserPermissionDetailsWithRoles, AllowedPermissionsByRole } from '@/types/UserPermissions';
import validator from 'validator';
import AppSelect from '@/components/v2/AppSelect.vue';
import { UserModalType, UserType } from '@/types/User.js';
import authService, { UpdateUserRoleRequest } from '@/services/v2/AuthService.js';
import { Commit } from '@/store/index.js';
import { StoreMutationType } from '@/types/StoreMutationType.js';
import PasswordUpdateModal from '@/components/PasswordUpdateModal.vue';

const props = defineProps<{
  show: boolean;
  type: string;
  sso: boolean;
  user?: UserPermissionDetailsWithRoles;
  permissions: AllowedPermissionsByRole[];
}>();

const emit = defineEmits<{
  (e: 'cancel'): void;
  (e: 'updated', hasError: boolean): void;
  (e: 'reset-password'): void;
  (e: 'update-password'): void;
  (e: 'created', hasError: boolean): void;
}>();

interface NewUserReq {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
}

const orderedPermissions = [
  'overview:read',
  'policies:read',
  'towers:read',
  'programs:read',
  'renewal:read',
  'claims:read',
  'documents:read',
  'documents:write',
  'analytics:read',
  'benefits:read',
  'fees:read',
  'notifications:read',
  'cardview:read',
  'aiverification:write',
];

const appRoles = ref<AppRole[]>([]);

const title = props.type === UserModalType.Create ? 'Create User' : 'Update User';

// UPDATE USER FORM VALIDATION
const updateFormSchema = z.object({
  firstName: z.string().trim(),
  lastName: z.string().trim(),
  email: z.string().refine((val) => validator.isEmail(val), { message: 'Invalid email' }),
  accounts: z.array(z.any()).optional(),
  parentAccounts: z.array(z.any()).optional(),
  carriers: z.array(z.string()).optional(),
  wholesalers: z.array(z.string()).optional(),
  role: z.string(),
});

type updateFormSchemaType = z.infer<typeof updateFormSchema>;

type UpdateValidFormFields = {
  [K in keyof updateFormSchemaType]: boolean;
};

const updateDefaultValidFormFields: UpdateValidFormFields = {
  firstName: true,
  lastName: true,
  email: true,
  accounts: true,
  carriers: true,
  wholesalers: true,
  parentAccounts: true,
  role: true,
};

class UpdateUserForm {
  firstName?: string;
  lastName?: string;
  email?: string;
  accounts?: AccountInfo[];
  parentAccounts?: AccountInfo[];
  carriers?: string[];
  wholesalers?: string[];
  role?: string;
}

//  CREATE USER FORM VALIDATION
const createFormSchema = z
  .object({
    users: z.array(
      z.object({
        firstName: z
          .string()
          .trim()
          .transform((value) => value.replaceAll(' ', '')),
        lastName: z
          .string()
          .trim()
          .transform((value) => value.replaceAll(' ', '')),
        email: z.string().refine((val) => validator.isEmail(val), { message: 'Invalid email' }),
        phone: z.optional(
          z
            .string()
            .refine((val) => !val || val === '' || validator.isMobilePhone(val, 'en-US'), { message: 'Invalid phone number' })
            .or(z.literal('')),
        ),
      }),
    ),
    accounts: z.array(z.any()).optional(),
    parentAccounts: z.array(z.any()).optional(),
    carriers: z.array(z.string()).optional(),
    wholesalerGroups: z.array(z.string()).optional(),
    role: z.string(),
  })
  .superRefine((data, ctx) => {
    // Validate phone for each user based on role
    data.users.forEach((user, index) => {
      if (data.role === 'admin') {
        // Phone is required for admin role
        if (!user.phone || user.phone.trim() === '' || !validator.isMobilePhone(user.phone, 'en-US')) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'A valid phone number is required for admin users',
            path: ['users', index, 'phone'],
          });
        }
      } else if (user.phone && user.phone.trim() !== '') {
        // Phone is optional for other roles, but if provided must be valid
        if (!validator.isMobilePhone(user.phone, 'en-US')) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Please enter a valid phone number',
            path: ['users', index, 'phone'],
          });
        }
      }
    });
  });

type createFormSchemaType = z.infer<typeof createFormSchema>;

type CreateValidFormFields = {
  [K in keyof createFormSchemaType]: boolean;
};

const createDefaultValidFormFields: CreateValidFormFields = {
  users: true,
  accounts: true,
  parentAccounts: true,
  carriers: true,
  wholesalerGroups: true,
  role: true,
};

class CreateUserForm {
  users: NewUserReq[] = [{ firstName: '', lastName: '', email: '', phone: '' }];
  accounts?: AccountInfo[];
  parentAccounts?: AccountInfo[];
  carriers?: string[];
  wholesalerGroups?: string[];
  role?: string;
}

// REFS
// -----
const updateForm = ref<UpdateUserForm>(new UpdateUserForm());
const updateFormErrors = ref<ZodFormattedError<updateFormSchemaType>>();
const updateValidFormFields = ref<UpdateValidFormFields>(structuredClone(updateDefaultValidFormFields));
const createForm = ref<CreateUserForm>({ role: undefined, users: [{ firstName: '', lastName: '', email: '', phone: '' }] });
const createFormErrors = ref<ZodFormattedError<createFormSchemaType>>();
const createValidFormFields = ref<CreateValidFormFields>(structuredClone(createDefaultValidFormFields));
const loading = ref(false);
const updatingUser = ref(false);
const creatingUser = ref(false);
const hasError = ref(false);

const accounts = ref<AccountSelectOption[]>([]);
const parentAccounts = ref<AccountSelectOption[]>([]);
const selectedAccount = ref('');
const selectedParentAccount = ref('');
const carrierOptions = ref<SelectOption[]>([]);
const selectedCarrierOption = ref('');
const wholesalerOptions = ref<SelectOption[]>([]);
const selectedWholesalerOption = ref('');
const userPermissions = ref<string[]>([]);
const userSelectedPermissions = ref<string[]>([]);
const showPasswordUpdateModal = ref(false);

// COMPUTED
// --------------
const selectedAccounts = computed<AccountSelectOption[]>(() => {
  return accounts.value.filter((a) => a.selected);
});

const selectedParentAccounts = computed<AccountSelectOption[]>(() => {
  return parentAccounts.value.filter((a) => a.selected);
});

const selectedCarrierOptions = computed<SelectOption[]>(() => {
  return carrierOptions.value.filter((a) => a.selected);
});

const selectedWholesalerOptions = computed<SelectOption[]>(() => {
  return wholesalerOptions.value.filter((a) => a.selected);
});

const formSelectedRole = computed(() => {
  if (props.type === UserModalType.Update) {
    return updateForm.value.role;
  } else if (props.type === UserModalType.Create) {
    return createForm.value.role;
  }
  return '';
});

const allowedPermissionsForUser = computed<UserPermission[]>(() => {
  const allowedPermissions = props.permissions.find((r) => r.roleName === formSelectedRole.value);
  if (allowedPermissions) {
    const permissions = allowedPermissions.permissions
      .map((p) => {
        const key = Object.keys(UserPermission).find((key) => UserPermission[key] === p);
        return key && UserPermission[key];
      })
      .filter((p) => p !== undefined) as UserPermission[];

    // Sort permissions according to orderedPermissions array
    return permissions.sort((a, b) => {
      const indexA = orderedPermissions.indexOf(a);
      const indexB = orderedPermissions.indexOf(b);
      if (indexA === -1) return 1; // Put unknown permissions at the end
      if (indexB === -1) return -1;
      return indexA - indexB;
    });
  }

  return [];
});

// WATCHERS
// --------------
watch(
  () => selectedAccounts.value,
  (accounts) => {
    updateForm.value.accounts = accounts.map((account) => {
      return {
        accountID: parseInt(account.value),
        accountName: account.label,
        isParent: account.isParent,
      };
    });
    createForm.value.accounts = accounts.map((account) => {
      return {
        accountID: parseInt(account.value),
        accountName: account.label,
        isParent: account.isParent,
      };
    });
  },
);

watch(
  () => selectedParentAccounts.value,
  (parentAccounts) => {
    updateForm.value.parentAccounts = parentAccounts.map((account) => {
      return {
        accountID: parseInt(account.value),
        accountName: account.label,
        isParent: account.isParent,
      };
    });
    createForm.value.parentAccounts = parentAccounts.map((account) => {
      return {
        accountID: parseInt(account.value),
        accountName: account.label,
        isParent: account.isParent,
      };
    });
  },
);

watch(
  () => createForm.value.role,
  async (role) => {
    userSelectedPermissions.value = [];
    if (role === 'market') {
      await loadCarrierOptions();
      await loadWholesalerOptions();
    }
  },
);

watch(
  () => updateForm.value.role,
  async (role) => {
    // clear permissions on user role change
    if (role && !props.user?.roles?.includes(role)) {
      userSelectedPermissions.value = [];
    }
    // if changed back to original role, set back to original perms
    if (role && props.user?.roles?.includes(role)) {
      userSelectedPermissions.value = props.user?.permissions ?? [];
    }
    if (role === 'market') {
      await loadCarrierOptions();
      await loadWholesalerOptions();
    }
    if (role && ['admin', 'broker', 'market'].includes(role)) {
      updateForm.value.accounts = [];
      updateForm.value.parentAccounts = [];
    }
  },
);

// LIFECYCLE
// --------------
onMounted(async () => {
  loading.value = true;
  addEventListener('keyup', handleCloseWithEscapeKey);

  const accountList = (await permissionService.getAllAccounts()).sort((a1, a2) => a1.accountName.localeCompare(a2.accountName));

  appRoles.value.push(...(await permissionService.getAppRoles()));

  if (accountList) {
    accounts.value = accountList.map((account) => {
      return {
        label: account.accountName,
        value: account.accountID.toString(),
        isParent: false,
        selected: props.user?.accounts?.some((a) => a.accountId === account.accountID && !a.parent) ?? false,
      };
    });

    parentAccounts.value = accountList
      .filter((account) => account.isParent)
      .map((parentAccount) => {
        return {
          label: parentAccount.accountName,
          value: parentAccount.accountID.toString(),
          isParent: parentAccount.isParent,
          selected: props.user?.accounts?.some((a) => a.accountId === parentAccount.accountID && a.parent) ?? false,
        };
      });
  }

  const nameSplit = props.user?.name?.trim().split(' ') ?? [];

  if (nameSplit.length > 0) {
    if (nameSplit.length === 1) {
      updateForm.value.firstName = nameSplit[0];
      updateForm.value.lastName = '';
    } else {
      updateForm.value.firstName = nameSplit.slice(0, -1).join(' ');
      updateForm.value.lastName = nameSplit[nameSplit.length - 1];
    }
  } else {
    updateForm.value.firstName = '';
    updateForm.value.lastName = '';
  }

  updateForm.value.email = props.user?.userEmail ?? '';
  updateForm.value.role = props.user?.roles[0] ?? '';
  updateForm.value.accounts =
    props.user?.accounts
      ?.filter((account) => !account.parent)
      .map((account) => ({
        accountID: account.accountId,
        accountName: account.accountName,
        isParent: account.parent,
      })) ?? [];
  updateForm.value.parentAccounts =
    props.user?.accounts
      ?.filter((account) => account.parent)
      .map((account) => ({
        accountID: account.accountId,
        accountName: account.accountName,
        isParent: account.parent,
      })) ?? [];

  if (props.user?.permissions) {
    userPermissions.value = props.user.permissions;
    userSelectedPermissions.value = props.user.permissions;
  }

  if (props.user?.roles.includes('market')) {
    await loadCarrierOptions();
    await loadWholesalerOptions();

    updateForm.value.carriers = props.user.carrierIds?.map((c) => c.toString()) ?? [];
    updateForm.value.wholesalers = props.user.wholesalerIds?.map((w) => w.toString()) ?? [];

    carrierOptions.value = carrierOptions.value.map((carrier) => {
      return {
        ...carrier,
        selected: updateForm.value.carriers?.includes(carrier.value) ?? false,
      };
    });

    wholesalerOptions.value = wholesalerOptions.value.map((wholesaler) => {
      return {
        ...wholesaler,
        selected: updateForm.value.wholesalers?.includes(wholesaler.value) ?? false,
      };
    });
  }

  if (createForm.value.role === 'market') {
    await loadCarrierOptions();
    await loadWholesalerOptions();
  }

  loading.value = false;
});

onUnmounted(() => {
  removeEventListener('keyup', handleCloseWithEscapeKey);
});

// METHODS
// --------------
function handleRemoveCarrier(carrier: SelectOption) {
  carrierOptions.value = carrierOptions.value.map((a) => {
    if (a.value === carrier.value) {
      return { ...a, selected: false };
    }
    return a;
  });
}

function handleRemoveWholesaler(wholesaler: SelectOption) {
  wholesalerOptions.value = wholesalerOptions.value.map((a) => {
    if (a.value === wholesaler.value) {
      return { ...a, selected: false };
    }
    return a;
  });
}

async function loadCarrierOptions() {
  if (carrierOptions.value.length > 0) {
    return;
  }

  carrierOptions.value = (await carrierService.list()).map((carrier) => {
    return {
      label: carrier.name,
      value: carrier.ID.toString(),
      selected: false,
    };
  });
}

async function loadWholesalerOptions() {
  if (wholesalerOptions.value.length > 0) {
    return;
  }

  wholesalerOptions.value = (await wholesalerGroupService.list()).map((wholesalerGroup) => {
    return {
      label: wholesalerGroup.name,
      value: wholesalerGroup.id.toString(),
      selected: false,
    };
  });
}

function haveRolesChanged() {
  return props.user?.roles && formSelectedRole.value && (!props.user?.roles.includes(formSelectedRole.value) || props.user?.roles.length > 1);
}

async function handleUpdateUser() {
  try {
    if (!validateForm()) {
      console.error('Failed to update user');
      return;
    }

    updatingUser.value = true;

    // market users should have no associated accounts
    if (formSelectedRole.value === 'market') {
      updateForm.value.accounts = [];
      updateForm.value.parentAccounts = [];
    }

    // ROLE CHANGE
    const userId = props.user?.userId;
    if (userId && haveRolesChanged()) {
      try {
        await authService.updateUserRole({
          userId: userId,
          roleName: formSelectedRole.value,
        } as UpdateUserRoleRequest);
      } catch (e) {
        Commit(StoreMutationType.SET_FLASH_MESSAGE, { text: 'Failed to update user roles', color: 'error' });
        hasError.value = true;
        emit('updated', hasError.value);
        updatingUser.value = false;
      }
    }

    // USER INFO, ACCOUNTS, CARRIER/WHOLESALER CHANGE
    // parent accounts can be linked to a user as a standalone account, ensure `isParent` is false
    const accounts = (updateForm.value.accounts ?? []).map((account) => ({ ...account, isParent: false }));
    const allAccounts = [...accounts, ...(updateForm.value.parentAccounts ?? [])];

    if (props.user?.userSubId) {
      await permissionService.updateUser(
        props.user.userSubId,
        updateForm.value.firstName + ' ' + updateForm.value.lastName,
        updateForm.value.email ?? '',
        allAccounts,
        formSelectedRole.value === 'market' ? (selectedCarrierOptions.value?.map((c) => Number(c.value)) ?? []) : [],
        formSelectedRole.value === 'market' ? (selectedWholesalerOptions.value?.map((w) => Number(w.value)) ?? []) : [],
      );
    }

    // PERMISSIONS CHANGE
    if (props.user && havePermsChanged() && props.user.userSubId) {
      const permsToAdd = userSelectedPermissions.value.filter((perm) => !userPermissions.value.includes(perm));
      const permsToRemove = userPermissions.value.filter((perm) => !userSelectedPermissions.value.includes(perm));

      try {
        const permissionCalls = permsToAdd
          .map(async (perm) => await permissionService.AddPermission(perm, props.user?.userSubId ?? ''))
          .concat(permsToRemove.map(async (perm) => await permissionService.DeletePermission(perm, props.user?.userSubId ?? '')));

        await Promise.all(permissionCalls);
      } catch (e) {
        Commit(StoreMutationType.SET_FLASH_MESSAGE, { text: 'Failed to update user permissions', color: 'error' });
        hasError.value = true;
        emit('updated', hasError.value);
        updatingUser.value = false;
      }
    }
  } catch (error) {
    console.error(error);
    hasError.value = true;
  } finally {
    emit('updated', hasError.value);
    updatingUser.value = false;
  }
}

async function handleCreateUser() {
  try {
    if (!validateForm()) {
      console.error('Failed to create user');
      hasError.value = true;
      return;
    }

    creatingUser.value = true;
    if (createForm.value.role === 'market') {
      for (let i = 0; i < createForm.value.users.length; i++) {
        await permissionService.CreateMarketUser(
          createForm.value.users[i].firstName + ' ' + createForm.value.users[i].lastName,
          createForm.value.users[i].email ?? '',
          selectedCarrierOptions.value.map((carrier) => Number(carrier.value)),
          selectedWholesalerOptions.value.map((wholesalerGroup) => Number(wholesalerGroup.value)),
        );
      }
    } else {
      const allAccounts = [...(createForm.value.accounts ?? []), ...(createForm.value.parentAccounts ?? [])];
      const allPermissions = userSelectedPermissions.value;
      for (let i = 0; i < createForm.value.users.length; i++) {
        // Format phone number: add +1 prefix if provided
        let formattedPhone: string | undefined = undefined;
        const userPhone = createForm.value.users[i]?.phone;
        if (userPhone && userPhone.trim() !== '') {
          const phone = userPhone.trim().replace(/\D/g, ''); // Remove non-digits
          if (phone.startsWith('1')) {
            formattedPhone = '+1' + phone.substring(1);
          } else {
            formattedPhone = '+1' + phone;
          }
        }
        await permissionService.CreateUserWithAccounts(
          createForm.value.users[i].firstName + ' ' + createForm.value.users[i].lastName,
          createForm.value.users[i].email ?? '',
          createForm.value.role ?? '',
          allAccounts,
          allPermissions,
          formattedPhone,
        );
      }
    }
  } catch (error) {
    console.error(error);
    hasError.value = true;
  } finally {
    emit('created', hasError.value);
    creatingUser.value = false;
  }
}

function havePermsChanged() {
  return userPermissions.value.sort().join() !== userSelectedPermissions.value.sort().join();
}

function handleCloseWithEscapeKey(e: KeyboardEvent): void {
  if (e.key === 'Escape') {
    // emit('cancel');
  }
}

//// Account Select
function handleAccountSelect(account: SelectOption) {
  accounts.value = accounts.value.map((a) => {
    if (a.value === account.value) {
      return { ...a, selected: true };
    }
    return a;
  });
  // Clear selection to allow rapid multi-select with combobox
  selectedAccount.value = '';
}

function handleAccountRemove(account: SelectOption) {
  accounts.value = accounts.value.map((a) => {
    if (a.value === account.value) {
      return { ...a, selected: false };
    }
    return a;
  });
}

//// Parent Account Select
function handleParentAccountSelect(account: SelectOption) {
  parentAccounts.value = parentAccounts.value.map((a) => {
    if (a.value === account.value) {
      return { ...a, selected: true };
    }
    return a;
  });
  // Clear selection to allow rapid multi-select with combobox
  selectedParentAccount.value = '';
}

function handleParentAccountRemove(account: SelectOption) {
  parentAccounts.value = parentAccounts.value.map((a) => {
    if (a.value === account.value) {
      return { ...a, selected: false };
    }
    return a;
  });
}

function validateForm(): boolean {
  if (props.type === UserModalType.Create) {
    const valid = createFormSchema.safeParse(createForm.value);
    createValidFormFields.value = structuredClone(createDefaultValidFormFields);

    if (!valid.success) {
      // set valid form fields to false
      createFormErrors.value = valid.error.format();

      for (const error of (valid as SafeParseError<createFormSchemaType>).error.issues) {
        createValidFormFields.value[error.path[0].toString() as keyof CreateValidFormFields] = false;
      }
    }

    return valid.success;
  } else {
    const valid = updateFormSchema.safeParse(updateForm.value);
    updateValidFormFields.value = structuredClone(updateDefaultValidFormFields);

    if (!valid.success) {
      // set valid form fields to false
      updateFormErrors.value = valid.error.format();

      for (const error of (valid as SafeParseError<updateFormSchemaType>).error.issues) {
        updateValidFormFields.value[error.path[0].toString() as keyof UpdateValidFormFields] = false;
      }
    }

    return valid.success;
  }
}

function handlePasswordOptions(option: string) {
  if (option === 'Reset Password') {
    emit('reset-password');
  } else {
    showPasswordUpdateModal.value = true;
  }
}

function handlePasswordUpdateSave(error: boolean) {
  showPasswordUpdateModal.value = false;
  if (error) {
    Commit(StoreMutationType.SET_FLASH_MESSAGE, { text: 'Failed to update password', color: 'error' });
  } else {
    Commit(StoreMutationType.SET_FLASH_MESSAGE, { text: 'Password updated successfully' });
    emit('updated', false);
  }
}

function handleCarrierSelect(carrier: SelectOption) {
  carrierOptions.value = carrierOptions.value.map((a) => {
    if (a.value === carrier.value) {
      return { ...a, selected: true };
    }
    return a;
  });
  // Clear selection to allow rapid multi-select with combobox
  selectedCarrierOption.value = '';
}

function handleWholesalerSelect(wholesaler: SelectOption) {
  wholesalerOptions.value = wholesalerOptions.value.map((a) => {
    if (a.value === wholesaler.value) {
      return { ...a, selected: true };
    }
    return a;
  });
  // Clear selection to allow rapid multi-select with combobox
  selectedWholesalerOption.value = '';
}

function handleCancel() {
  emit('cancel');
  hasError.value = false;
  updateFormErrors.value = undefined;
  createFormErrors.value = undefined;
  updateValidFormFields.value = structuredClone(updateDefaultValidFormFields);
  createValidFormFields.value = structuredClone(createDefaultValidFormFields);
  userSelectedPermissions.value = [];
  updateForm.value = new UpdateUserForm();
  createForm.value = new CreateUserForm();
}

function addUserToCreateRequest() {
  createForm.value.users.push({ firstName: '', lastName: '', email: '', phone: '' });
}

function removeUser(index) {
  createForm.value.users.splice(index, 1);
}
</script>

<template>
  <AppModal
    :show="show"
    :title="title"
    size="lg"
    :handle-cancel="handleCancel"
    :handle-action="props.type === UserModalType.Create ? handleCreateUser : handleUpdateUser"
    :loading="updatingUser"
    :modal-class="props.type === UserModalType.Create ? 'tw:max-h-[85vh] tw:overflow-y-auto' : ''"
    :action-button-label="props.type === UserModalType.Create ? 'Create' : 'Update'">
    <template #content>
      <div class="tw:flex tw:flex-col tw:gap-4 tw:p-1">
        <p
          class="tw:text-sm tw:text-gray-600 tw:italic"
          v-if="!props.sso">
          Users will be emailed a temporary password after creation
        </p>
        <p v-else>{{ props.user?.userEmail }}</p>

        <!-- USER INFO (update) -->
        <div
          v-if="props.type === UserModalType.Update && !props.sso"
          class="tw:flex tw:items-center tw:gap-4">
          <div class="tw:w-3/12">
            <label class="tw:label">
              <span class="tw:label-text tw:text-xs tw:text-gray-500"> First Name <span class="tw:font-bold tw:text-primary">*</span> </span>
            </label>
            <input
              type="text"
              v-model="updateForm.firstName"
              class="tw:input tw:input-sm tw:w-full tw:min-w-0 tw:border tw:border-solid" />
            <span
              v-show="props.type === UserModalType.Update && updateFormErrors?.firstName?._errors"
              class="tw:label-text-alt tw:text-xs tw:text-red-500">
              {{ updateFormErrors?.firstName?._errors.join(', ') }}
            </span>
          </div>

          <div class="tw:w-3/12">
            <label class="tw:label">
              <span class="tw:label-text tw:text-xs tw:text-gray-500">Last Name <span class="tw:font-bold tw:text-primary">*</span> </span>
            </label>
            <input
              type="text"
              v-model="updateForm.lastName"
              class="tw:input tw:input-sm tw:w-full tw:min-w-0 tw:border tw:border-solid" />
            <span
              v-show="props.type === UserModalType.Update && updateFormErrors?.lastName?._errors"
              class="tw:label-text-alt tw:text-xs tw:text-red-500">
              {{ updateFormErrors?.lastName?._errors.join(', ') }}
            </span>
          </div>

          <div class="tw:w-6/12">
            <label class="tw:label">
              <span class="tw:label-text tw:text-xs tw:text-gray-500">Email <span class="tw:font-bold tw:text-primary">*</span> </span>
            </label>
            <input
              type="text"
              v-model="updateForm.email"
              class="tw:input tw:input-sm tw:w-full tw:min-w-0 tw:border tw:border-solid" />
            <span
              v-show="props.type === UserModalType.Update && updateFormErrors?.email?._errors"
              class="tw:label-text-alt tw:text-xs tw:text-red-500">
              {{ updateFormErrors?.email?._errors.join(', ') }}
            </span>
          </div>
        </div>

        <!-- USER INFO (create) -->
        <div v-if="props.type === UserModalType.Create">
          <div
            v-for="(_, index) in createForm.users"
            :key="index"
            class="tw:flex tw:gap-2"
            :class="index > 0 ? 'tw:mt-2' : ''">
            <div class="tw:w-3/12">
              <label
                v-if="index === 0"
                class="tw:label">
                <span class="tw:label-text tw:text-xs tw:text-gray-500"> First Name <span class="tw:font-bold tw:text-primary">*</span> </span>
              </label>
              <input
                type="text"
                v-model="createForm.users[index].firstName"
                class="tw:input tw:input-sm tw:w-full tw:min-w-0 tw:border tw:border-solid" />
              <span
                v-show="(createFormErrors?.users ?? [])[index]?.firstName?._errors"
                class="tw:label-text-alt tw:text-xs tw:text-red-500">
                {{ (createFormErrors?.users ?? [])[index]?.firstName?._errors.join(', ') }}
              </span>
            </div>

            <div class="tw:w-3/12">
              <label
                v-if="index === 0"
                class="tw:label">
                <span class="tw:label-text tw:text-xs tw:text-gray-500">Last Name <span class="tw:font-bold tw:text-primary">*</span> </span>
              </label>
              <input
                type="text"
                v-model="createForm.users[index].lastName"
                class="tw:input tw:input-sm tw:w-full tw:min-w-0 tw:border tw:border-solid" />
              <span
                v-show="(createFormErrors?.users ?? [])[index]?.lastName?._errors"
                class="tw:label-text-alt tw:text-xs tw:text-red-500">
                {{ (createFormErrors?.users ?? [])[index]?.lastName?._errors.join(', ') }}
              </span>
            </div>

            <div class="tw:w-4/12">
              <label
                v-if="index === 0"
                class="tw:label">
                <span class="tw:label-text tw:text-xs tw:text-gray-500">Email <span class="tw:font-bold tw:text-primary">*</span> </span>
              </label>

              <input
                type="text"
                v-model="createForm.users[index].email"
                class="tw:input tw:input-sm tw:w-full tw:min-w-0 tw:border tw:border-solid" />
              <span
                v-show="(createFormErrors?.users ?? [])[index]?.email?._errors"
                class="tw:label-text-alt tw:text-xs tw:text-red-500">
                {{ (createFormErrors?.users ?? [])[index]?.email?._errors.join(', ') }}
              </span>
            </div>

            <div class="tw:w-3/12">
              <label
                v-if="index === 0"
                class="tw:label">
                <span class="tw:label-text tw:text-xs tw:text-gray-500">
                  Phone
                  <span
                    v-if="createForm.role === 'admin'"
                    class="tw:font-bold tw:text-primary"
                    >*</span
                  >
                </span>
              </label>

              <input
                type="tel"
                v-model="createForm.users[index].phone"
                placeholder="(555) 123-4567"
                class="tw:input tw:input-sm tw:w-full tw:min-w-0 tw:border tw:border-solid" />
              <span
                v-show="(createFormErrors?.users ?? [])[index]?.phone?._errors"
                class="tw:label-text-alt tw:text-xs tw:text-red-500">
                {{ (createFormErrors?.users ?? [])[index]?.phone?._errors.join(', ') }}
              </span>
            </div>

            <div class="tw:flex tw:w-1/12 tw:items-center tw:rounded">
              <button
                v-if="index > 0"
                @click="removeUser(index)"
                class="tw:rounded tw:p-2 tw:opacity-20 tw:transition-all tw:hover:bg-slate-200 tw:hover:text-red-400 tw:hover:opacity-100">
                <Trash2 :size="16"></Trash2>
              </button>
            </div>
          </div>
        </div>
        <a
          v-if="props.type === UserModalType.Create && createForm.users.length < 5"
          @click="addUserToCreateRequest"
          class="tw:cursor-pointer tw:p-0.5 tw:text-sm"
          >Add another user</a
        >

        <!-- ROLES -->
        <!-- create -->
        <div v-if="props.type === UserModalType.Create">
          <label class="tw:label">
            <span class="tw:label-text tw:text-xs tw:text-gray-500">User Role <span class="tw:font-bold tw:text-primary">*</span> </span>
          </label>
          <AppSelect
            v-model="createForm.role"
            :options="
              appRoles.map((role) => ({
                label: role.name.charAt(0).toUpperCase() + role.name.slice(1),
                value: role.name,
              }))
            "
            placeholder="Select a role"
            class="tw:h-8 tw:w-full" />
          <label class="tw:label">
            <span class="tw:label-text-alt tw:text-xs tw:text-gray-500">
              {{ appRoles.find((r) => r.name === createForm.role)?.description }}
            </span>
          </label>
        </div>
        <!-- update (manual) -->
        <div v-else-if="props.type === UserModalType.Update && !props.sso">
          <label class="tw:label">
            <span class="tw:label-text tw:text-xs tw:text-gray-500">User Role <span class="tw:font-bold tw:text-primary">*</span> </span>
          </label>
          <AppSelect
            v-model="updateForm.role"
            :options="
              appRoles.map((role) => ({
                label: role.name.charAt(0).toUpperCase() + role.name.slice(1),
                value: role.name,
              }))
            "
            class="tw:h-8 tw:w-full" />
          <label class="tw:label">
            <span class="tw:label-text-alt tw:text-xs tw:text-gray-500">
              {{ appRoles.find((r) => r.name === updateForm.role)?.description }}
            </span>
          </label>
        </div>
        <!-- update (sso) -->
        <div v-else-if="props.type === UserModalType.Update && props.sso">
          <label class="tw:label">
            <span class="tw:label-text tw:text-xs tw:text-gray-500">User Role <span class="tw:font-bold tw:text-primary">*</span> </span>
          </label>
          <AppSelect
            v-model="updateForm.role"
            :options="
              SECURITY_ROLES.map((role) => ({
                label: role.charAt(0).toUpperCase() + role.slice(1),
                value: role,
              }))
            "
            class="tw:h-8 tw:w-full" />
          <label class="tw:label">
            <span class="tw:label-text-alt tw:text-xs tw:text-gray-500">
              {{ appRoles.find((r) => r.name === updateForm.role)?.description }}
            </span>
          </label>
        </div>

        <!-- ACCOUNTS-->
        <div
          class="tw:w-full"
          :class="{ 'tw:hidden': formSelectedRole && ['market', 'admin', 'broker'].includes(formSelectedRole) }">
          <div class="tw:flex tw:flex-col">
            <label class="tw:label">
              <span class="tw:label-text tw:text-xs tw:text-gray-500">Accounts</span>
            </label>

            <div
              v-if="selectedAccounts.length > 0"
              class="tw:flex tw:flex-wrap tw:pb-2">
              <div
                v-for="account in selectedAccounts"
                :key="account.value"
                @click="handleAccountRemove(account)"
                class="tw:group tw:badge tw:flex tw:h-fit tw:cursor-pointer tw:gap-1 tw:badge-ghost tw:badge-xs tw:hover:bg-red-100">
                <div class="tw:p-1">{{ account.label }}</div>
                <X
                  :size="12"
                  class="tw:group-hover:stroke-red-600" />
              </div>
            </div>

            <Combobox
              v-model="selectedAccount"
              :options="accounts"
              :placeholder="'Select Accounts'"
              @selected="handleAccountSelect"
              input-class="tw:input-sm" />
          </div>
        </div>

        <!-- PARENT ACCOUNTS -->
        <div
          class="tw:w-full"
          :class="{ 'tw:hidden': formSelectedRole && ['market', 'admin', 'broker'].includes(formSelectedRole) }">
          <div class="tw:flex tw:flex-col">
            <label class="tw:label">
              <span class="tw:label-text tw:text-xs tw:text-gray-500">Parent Accounts</span>
            </label>

            <div
              v-if="selectedParentAccounts.length > 0"
              class="tw:flex tw:flex-wrap tw:pb-2">
              <div
                v-for="account in selectedParentAccounts"
                :key="account.value"
                @click="handleParentAccountRemove(account)"
                class="tw:group tw:badge tw:flex tw:h-fit tw:cursor-pointer tw:gap-1 tw:badge-ghost tw:badge-xs tw:hover:bg-red-100">
                <div class="tw:p-1">{{ account.label }}</div>
                <X
                  :size="12"
                  class="tw:group-hover:stroke-red-600" />
              </div>
            </div>

            <Combobox
              v-model="selectedParentAccount"
              :options="parentAccounts"
              :placeholder="'Select Accounts'"
              @selected="handleParentAccountSelect"
              input-class="tw:input-sm" />
          </div>
        </div>
        <!-- Carriers -->
        <div
          class="tw:w-full"
          :class="{ 'tw:hidden': formSelectedRole != 'market' }">
          <div class="tw:flex tw:flex-col">
            <label class="tw:label">
              <span class="tw:label-text tw:text-xs tw:text-gray-500">Carriers</span>
            </label>

            <div
              v-if="selectedCarrierOptions.length > 0"
              class="tw:flex tw:flex-wrap tw:pb-2">
              <div
                v-for="carrier in selectedCarrierOptions"
                :key="carrier.value"
                @click="handleRemoveCarrier(carrier)"
                class="tw:group tw:badge tw:flex tw:h-fit tw:cursor-pointer tw:gap-1 tw:badge-ghost tw:badge-xs tw:hover:bg-red-100">
                <div class="tw:p-1">{{ carrier.label }}</div>
                <X
                  :size="12"
                  class="tw:group-hover:stroke-red-600" />
              </div>
            </div>

            <Combobox
              v-model="selectedCarrierOption"
              :options="carrierOptions"
              :placeholder="'Select Carrier'"
              @selected="handleCarrierSelect"
              input-class="tw:input-sm" />
          </div>
        </div>

        <!-- Wholesalers -->
        <div
          class="tw:w-full"
          :class="{ 'tw:hidden': formSelectedRole != 'market' }">
          <div class="tw:flex tw:flex-col">
            <label class="tw:label">
              <span class="tw:label-text tw:text-xs tw:text-gray-500">Wholesalers</span>
            </label>

            <div
              v-if="selectedWholesalerOptions.length > 0"
              class="tw:flex tw:flex-wrap tw:pb-2">
              <div
                v-for="wholesaler in selectedWholesalerOptions"
                :key="wholesaler.value"
                @click="handleRemoveWholesaler(wholesaler)"
                class="tw:group tw:badge tw:flex tw:h-fit tw:cursor-pointer tw:gap-1 tw:badge-ghost tw:badge-xs tw:hover:bg-red-100">
                <div class="tw:p-1">{{ wholesaler.label }}</div>
                <X
                  :size="12"
                  class="tw:group-hover:stroke-red-600" />
              </div>
            </div>

            <Combobox
              v-model="selectedWholesalerOption"
              :options="wholesalerOptions"
              :placeholder="'Select Wholesaler'"
              @selected="handleWholesalerSelect"
              input-class="tw:input-sm" />
          </div>
        </div>
      </div>

      <!-- App Permissions -->
      <div
        v-if="allowedPermissionsForUser && allowedPermissionsForUser.length > 0"
        class="tw:w-full">
        <label class="tw:label">
          <span class="tw:label-text tw:text-xs tw:text-gray-500">App Permissions</span>
        </label>
        <div class="tw:flex tw:max-h-52 tw:flex-col tw:overflow-y-auto tw:rounded-lg tw:border tw:border-solid tw:border-gray-200 tw:shadow-xs">
          <label
            v-for="perm in allowedPermissionsForUser"
            :key="perm"
            class="tw:label tw:cursor-pointer tw:rounded-lg tw:px-0 tw:py-0.5">
            <div class="tw:flex tw:w-full tw:items-center tw:rounded-lg tw:px-4 tw:py-1 tw:text-left tw:hover:bg-base-200">
              <div class="tw:flex tw:w-full tw:items-center tw:justify-between tw:gap-2">
                <div class="tw:flex tw:w-full tw:flex-col">
                  <div class="tw:text-md tw:grow">{{ perm }}</div>
                  <div class="tw:text-xs tw:text-wrap tw:text-gray-500">{{ UserPermissionDescriptions[perm] }}</div>
                </div>
                <input
                  type="checkbox"
                  v-model="userSelectedPermissions"
                  :value="perm"
                  class="tw:checkbox tw:border tw:border-solid tw:checkbox-sm tw:checkbox-primary" />
              </div>
            </div>
          </label>
        </div>
      </div>
    </template>
    <template
      v-if="props.type === UserModalType.Update && props.user && props.user.userTypeId !== UserType.SSO"
      #extra-actions>
      <app-button-select
        primary
        :select-options="['Reset Password', 'Update Password']"
        @select-option="
          (option) => {
            handlePasswordOptions(option);
          }
        "
        class="tw:mr-auto tw:ml-0"
        dropdownPosition="top">
      </app-button-select>
    </template>
  </AppModal>

  <PasswordUpdateModal
    v-if="props.type === UserModalType.Update && props.user"
    :show="showPasswordUpdateModal"
    :user-account="props.user"
    @close="showPasswordUpdateModal = false"
    @save="handlePasswordUpdateSave" />
</template>
